import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { Button, Form, Offcanvas } from "react-bootstrap";
import { ScaleLoader } from "react-spinners";
import { toast } from "react-toastify";
import AnomalyService from "../../../core/service/AnomalyService";
import { resolveAnomalyRuleLabel } from "../../../core/constants/anomalyRuleCodes";
import { formatAnomalyRecordDisplayPeriod } from "./anomalyDateUtils";
import AnomalyInfoTip from "./AnomalyInfoTip";
import {
  getInitialFixUom,
  getInitialCorrectedValue,
  getExpectedValueTooltip,
  getLockedFixUom,
  isUomChangeRule,
} from "./anomalyFixUtils";
import "./AnomalyFixModal.scss";

const AnomalyFixModal = ({
  t,
  show,
  recordId,
  onHide,
  onSuccess,
  onViewTimeSeries,
}) => {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [correctedValue, setCorrectedValue] = useState("");
  const [uom, setUom] = useState("");
  const [remark, setRemark] = useState("");
  const [inlineError, setInlineError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!show || !recordId) {
      setDetail(null);
      setCorrectedValue("");
      setUom("");
      setRemark("");
      setInlineError("");
      return;
    }

    setLoadingDetail(true);
    setDetail(null);
    setInlineError("");

    AnomalyService.getById(recordId)
      .then((res) => {
        const data = res.data;
        setDetail(data);
        setCorrectedValue(getInitialCorrectedValue(data));
        setUom(getInitialFixUom(data));
        setRemark("");
      })
      .catch(() => {
        toast.error(t("AnomalyDetection.detailError.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
        onHide();
      })
      .finally(() => setLoadingDetail(false));
  }, [show, recordId, t, onHide]);

  const handleSubmit = () => {
    if (!detail?.fixable) return;

    const trimmedValue = correctedValue.trim();
    if (trimmedValue === "") {
      setInlineError(t("AnomalyDetection.fixValueRequired.label"));
      return;
    }
    if (isUomChangeRule(detail.ruleCode) && !uom) {
      setInlineError(t("AnomalyDetection.fixUomRequired.label"));
      return;
    }

    const resolvedUom = isUomChangeRule(detail.ruleCode)
      ? uom
      : getLockedFixUom(detail);

    if (!resolvedUom) {
      setInlineError(t("AnomalyDetection.fixUomRequired.label"));
      return;
    }

    const numericValue = Number(trimmedValue);
    if (Number.isNaN(numericValue)) {
      setInlineError(t("AnomalyDetection.fixValueRequired.label"));
      return;
    }
    if (numericValue < 0) {
      setInlineError(t("AnomalyDetection.fixValueNegative.label"));
      return;
    }

    setInlineError("");
    setIsSubmitting(true);

    const payload = {
      correctedValue: numericValue,
      uom: resolvedUom,
    };
    if (remark.trim()) {
      payload.remark = remark.trim();
    }

    AnomalyService.fix(detail.id, payload)
      .then(() => {
        toast.success(t("AnomalyDetection.fixSuccess.label"), {
          position: toast.POSITION.TOP_RIGHT,
        });
        onHide();
        onSuccess?.();
      })
      .catch((error) => {
        const apiMsg = error?.response?.data?.message;
        setInlineError(apiMsg || t("AnomalyDetection.fixError.label"));
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleCorrectedValueChange = (e) => {
    const next = e.target.value;
    if (next === "") {
      setCorrectedValue("");
      return;
    }
    if (next.includes("-")) return;
    const num = Number(next);
    if (!Number.isNaN(num) && num < 0) return;
    setCorrectedValue(next);
  };

  const uomEditable = detail ? isUomChangeRule(detail.ruleCode) : false;
  const lockedUom = detail ? getLockedFixUom(detail) : "";
  const uomOptions = detail?.relevantUoms?.length
    ? detail.relevantUoms
    : uom
      ? [uom]
      : lockedUom
        ? [lockedUom]
        : [];

  const expectedValueTooltip = detail ? getExpectedValueTooltip(detail, t) : "";

  const renderContextLine = () => {
    if (!detail) return null;
    const parts = [
      detail.metricName,
      detail.branchName,
      formatAnomalyRecordDisplayPeriod(detail),
    ].filter(Boolean);
    return parts.join(" · ");
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      backdrop
      scroll
      className="anomaly-fix-drawer"
      data-testid="anomaly-fix-drawer"
    >
      <Offcanvas.Header closeButton className="fix-drawer-header">
        <div className="fix-drawer-header-inner">
          <span className="fix-drawer-eyebrow">
            {t("AnomalyDetection.fixAction.label")}
          </span>
          <Offcanvas.Title className="fix-drawer-title">
            {t("AnomalyDetection.fixModal.label")}
          </Offcanvas.Title>
          <p className="fix-drawer-subtitle">
            {t("AnomalyDetection.fixModalSubtitle.label")}
          </p>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="fix-drawer-body">
        {loadingDetail ? (
          <div className="fix-loading-wrap">
            <ScaleLoader color="#3b82f6" />
          </div>
        ) : detail ? (
          <>
            {inlineError && (
              <div className="fix-inline-error" role="alert">
                {inlineError}
              </div>
            )}

            <p className="fix-context-line">{renderContextLine()}</p>

            {!detail.fixable ? (
              <div className="fix-non-fixable-panel">
                <h6 className="fix-non-fixable-title">
                  {t("AnomalyDetection.nonFixableTitle.label")}
                </h6>
                <p className="fix-non-fixable-message">
                  {t("AnomalyDetection.nonFixableMessage.label")}
                </p>
                {detail.description && (
                  <p className="fix-issue-text">{detail.description}</p>
                )}
                {onViewTimeSeries && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      onHide();
                      onViewTimeSeries(detail);
                    }}
                  >
                    {t("AnomalyDetection.viewTimeSeries.label")}
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="fix-issue-panel">
                  <span className="fix-issue-label">
                    {t("AnomalyDetection.fixIssueSummary.label")}
                  </span>
                  {detail.ruleCode && (
                    <span className="fix-issue-rule">
                      {resolveAnomalyRuleLabel(t, detail.ruleCode)}
                    </span>
                  )}
                  {detail.description && (
                    <p className="fix-issue-text">{detail.description}</p>
                  )}
                  {detail.comparison?.summary && (
                    <p className="fix-comparison-line">
                      {detail.comparison.summary}
                    </p>
                  )}
                </div>

                <div className="fix-form-panel">
                  <h6 className="fix-form-panel-title">
                    {t("AnomalyDetection.fixFormSection.label")}
                  </h6>

                  <div className="fix-value-flow">
                    <div className="fix-value-block fix-value-block--current">
                      <span className="fix-value-block-label">
                        {t("AnomalyDetection.fixCurrentValue.label")}
                      </span>
                      <span className="fix-value-block-value">
                        {detail.currentValue ?? "—"}
                      </span>
                    </div>
                    <span className="fix-value-arrow" aria-hidden="true">
                      →
                    </span>
                    <div className="fix-value-block fix-value-block--corrected">
                      <span className="fix-value-block-label label-with-tip">
                        {t("AnomalyDetection.correctedValueInput.label")}
                        {expectedValueTooltip && (
                          <AnomalyInfoTip text={expectedValueTooltip} />
                        )}
                      </span>
                      <Form.Control
                        type="number"
                        step="any"
                        min={0}
                        value={correctedValue}
                        onChange={handleCorrectedValueChange}
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "Minus") {
                            e.preventDefault();
                          }
                        }}
                        className="fix-value-input"
                      />
                    </div>
                  </div>

                  <Form.Group className="fix-form-group">
                    <Form.Label>
                      {t("AnomalyDetection.uomInput.label")}
                    </Form.Label>
                    {uomEditable ? (
                      <Form.Select
                        value={uom}
                        onChange={(e) => setUom(e.target.value)}
                        required
                      >
                        {uomOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <div className="fix-uom-readonly">
                        {lockedUom || uom || "—"}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="fix-form-group">
                    <Form.Label>
                      {t("AnomalyDetection.fixRemarkInput.label")}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={remark}
                      onChange={(e) => setRemark(e.target.value)}
                      placeholder={t("AnomalyDetection.fixRemarkInput.label")}
                    />
                  </Form.Group>
                </div>
              </>
            )}
          </>
        ) : null}
      </Offcanvas.Body>

      {detail?.fixable && (
        <div className="fix-drawer-footer">
          <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
            {t("Cancel.label")}
          </Button>
          <Button
            variant="primary"
            className="sbmBtn fix-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting || loadingDetail}
          >
            {isSubmitting ? (
              <ScaleLoader color="#fff" height={14} width={2} />
            ) : (
              t("AnomalyDetection.fixSubmit.label")
            )}
          </Button>
        </div>
      )}
    </Offcanvas>
  );
};

export default withTranslation()(AnomalyFixModal);
